import { ENotAuthNavigation } from '@hts/shared/enums';
import { useIsMobile, useShouldRenderOnCurrentView } from '@hts/shared/libs';
import {
  IMappedSubmenuCategory,
  IPageDepartmentSectionEntry,
  IPageDepartmentSectionItem,
} from '@hts/shared/types';
import { HtsLink, HtsTypography, LinkElement } from '@hts/shared/ui';
import { memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styles from '../../styles/Footer/MainContent.module.scss';

interface IMainContentProps {
  simchasCategories?: IMappedSubmenuCategory[];
  occasionsCategories?: IMappedSubmenuCategory[];
  pageDepartment?: IPageDepartmentSectionEntry | null;
}

export const MainContent: React.FC<IMainContentProps> = memo(
  ({ simchasCategories = [], occasionsCategories = [], pageDepartment }) => {
    const isMobile = useIsMobile();

    const shouldRenderPageDepartment = useShouldRenderOnCurrentView(
      pageDepartment?.shouldShowOnDesktop,
      pageDepartment?.shouldShowOnMobile
    );

    const pageDepartmentItems = shouldRenderPageDepartment
      ? (pageDepartment?.sectionItems ?? [])
      : [];

    const renderPDItem = useCallback(
      ({ id, link, title }: IPageDepartmentSectionItem, index: number) => (
        <li key={`${id}-${index}`}>
          <Link to={link}>
            <HtsTypography variant="body2" fontWeight="500" color="white">
              {title}
            </HtsTypography>
          </Link>
        </li>
      ),
      []
    );

    const renderCategoryColumn = useCallback((categories: IMappedSubmenuCategory[]) => {
      const columns: React.ReactNode[] = [];
      const itemsPerColumns = 6;

      for (let i = 0; i < categories.length; i += itemsPerColumns) {
        columns.push(
          <ul key={i / itemsPerColumns} className={styles.categoriesColumn}>
            {categories
              .slice(i, i + itemsPerColumns)
              .map(({ id, categories, fileUrl: mediaPath, name, slug }, index) => (
                <li key={`${id}-${index}`}>
                  <HtsLink
                    label={name}
                    redirectTo={`${ENotAuthNavigation.CATEGORY_OF_CARD}/${slug}`}
                    typographyProps={{ variant: 'body2', fontWeight: '300', color: 'white' }}
                  />
                </li>
              ))}
          </ul>
        );
      }

      return columns;
    }, []);

    const renderMobilePDItem = useCallback(
      (items: IPageDepartmentSectionItem[]) => {
        const columns: React.ReactNode[] = [];
        const totalColumns = 2;
        const itemsPerColumns = Math.ceil(items.length / totalColumns);

        for (let i = 0; i < items.length; i += itemsPerColumns) {
          columns.push(
            <ul key={i}>
              {items.slice(i, i + itemsPerColumns).map((item, index) => renderPDItem(item, index))}
            </ul>
          );
        }

        return columns;
      },
      [renderPDItem]
    );

    return (
      <div className={styles.container}>
        <div className={styles.leftContainer}>
          <div className={styles.logoContainer}>
            <Link to={'/'}>
              <img
                className={styles.logoImage}
                src="/assets/images/ihopetoseeyou-logo.png"
                alt="Ihopetoseeyou.com"
              />
            </Link>
          </div>

          <div>
            <HtsTypography variant="subtitle" color="white">
              Inviting your friends and family to your “Simcha” shouldn’t be a hassle. Whether
              you’re making a Zucher, Vachnacht, Kiddish, Bar/Bat Mitzvah, Wedding, etc. Create
              beautiful custom invitations on our user-friendly website with hundreds of templates
              to choose from with just a few clicks. And say “I hope to see you” by my “Simcha” with
              confidence.
            </HtsTypography>
          </div>

          <div className={styles.linksContainer}>
            <LinkElement type="help" colorType="white" />
            <LinkElement type="faq" colorType="white" />
          </div>
        </div>

        <div className={styles.rightContainer}>
          <div>
            <div className={styles.topContainer}>
              <div className={styles.allDataCategories}>
                <HtsTypography variant="body2" color="white" fontWeight="500">
                  Simchas
                </HtsTypography>

                <div className={styles.categoriesContainer}>
                  {renderCategoryColumn(simchasCategories)}
                </div>
              </div>

              <div className={styles.allDataCategories}>
                <HtsTypography variant="body2" color="white" fontWeight="500">
                  Occasions
                </HtsTypography>

                <div className={styles.categoriesContainer}>
                  {renderCategoryColumn(occasionsCategories)}
                </div>
              </div>
            </div>

            {isMobile ? (
              <div className={styles.bottomColumns}>{renderMobilePDItem(pageDepartmentItems)}</div>
            ) : (
              <ul className={styles.bottomContainer}>{pageDepartmentItems.map(renderPDItem)}</ul>
            )}
          </div>
        </div>
      </div>
    );
  }
);
